// Cliente del backend propio. El front NUNCA llama a openfootball directo:
// siempre pasa por estos endpoints (proxy + cache).
//
// En dev, package.json "proxy" redirige /api → http://localhost:8080.
// En prod, configurar REACT_APP_API_BASE.
import { getWriteToken } from '../lib/identity';

const BASE = process.env.REACT_APP_API_BASE ?? '';

// Lectura síncrona del cache local (para mostrar al instante en la 2da navegación).
export function cacheRead<T>(path: string): T | null {
  try {
    const raw = localStorage.getItem('m26:' + path);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

// Cache offline en localStorage: red primero; si la red falla, sirve la última copia.
async function get<T>(path: string): Promise<T> {
  const ckey = 'm26:' + path;
  try {
    const res = await fetch(`${BASE}/api${path}`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as { error?: string }).error ?? `Error ${res.status}`);
    }
    const json = (await res.json()) as T;
    try {
      localStorage.setItem(ckey, JSON.stringify(json));
    } catch {
      /* sin espacio: ignorar */
    }
    return json;
  } catch (err) {
    if (err instanceof TypeError) {
      // Falla de RED (no de API): servir copia local si existe.
      const raw = localStorage.getItem(ckey);
      if (raw) return JSON.parse(raw) as T;
    }
    throw err;
  }
}

// GET sin cache offline (para datos de administración, que no deben servir copia vieja).
async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}/api${path}`);
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? `Error ${res.status}`);
  }
  return (await res.json()) as T;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}/api${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? `Error ${res.status}`);
  }
  return (await res.json()) as T;
}

async function postForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${BASE}/api${path}`, { method: 'POST', body: form });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? `Error ${res.status}`);
  }
  return (await res.json()) as T;
}

// --- Tipos (espejo del contrato del backend) ---

export type MatchStatus = 'finished' | 'live' | 'upcoming';

export interface TeamRef {
  name: string;
  placeholder: boolean; // true = "2A", "W74" (placeholder de llave)
  resolved: string | null; // selección real (proyectada o confirmada), o null
  projected: boolean; // true = proyección desde un grupo aún en juego (puede cambiar)
  label: string | null; // texto del placeholder: "1º Grupo J", "Mejor 3º (A/B/C/D/F)"
}

export interface Goal {
  name: string;
  minute: string | null;
  penalty: boolean;
  owngoal: boolean;
}

export interface Score {
  ft: [number, number]; // resultado a los 90'
  ht: [number, number] | null;
  et?: [number, number] | null; // tras alargue
  pen?: [number, number] | null; // tanda de penales (definición de la llave)
}

export interface Stage {
  key: string; // 'r32' | 'r16' | 'qf' | 'sf' | 'third' | 'final'
  order: number;
  label: string;
}

export interface Match {
  id: number;
  phase: 'group' | 'knockout';
  round: string | null;
  stage: Stage | null;
  group: string | null; // "A".."L"
  num: number | null;
  date: string | null;
  time: string | null;
  kickoffUtc: string | null;
  ground: string | null;
  team1: TeamRef;
  team2: TeamRef;
  score: Score | null;
  status: MatchStatus;
  goals1: Goal[];
  goals2: Goal[];
  live?: LiveInfo | null; // presente solo si una fuente de vivo superpuso el dato
}

export interface LiveInfo {
  clock: string | null; // minuto en curso, ej. "67'"
  detail: string | null; // "FT", "HT", "Final"…
  source: string; // 'espn'
}

export interface LiveStatus {
  enabled: boolean; // la fuente de vivo está configurada
  source: string; // 'espn' | 'none' | …
  ok: boolean; // hay un snapshot fresco (tiempo real funcionando)
  ageSeconds: number | null; // antigüedad del snapshot servido
}

export interface Weather {
  ok: boolean;
  temp?: number; // °C
  code?: number; // WMO weather code
}

export interface MatchPlayer {
  name: string;
  pos: string;
  num: string | null;
}
export interface MatchLineup {
  formation: string | null;
  starters: MatchPlayer[];
  bench: MatchPlayer[];
}
export interface MatchStat {
  label: string;
  team1: string;
  team2: string;
}
export interface MatchKeyEvent {
  clock: string;
  text: string;
  type: string;
}
export interface FormGame {
  result: 'W' | 'L' | 'D' | string;
  score: string;
  opp: string;
  date: string;
  gf: number; // goles a favor
  ga: number; // goles en contra
}
export interface H2H {
  team1Wins: number;
  team2Wins: number;
  draws: number;
  total: number;
  games: { date: string; score: string; result: string; comp: string }[];
}
export interface TeamRank {
  rank: number; // posición en el ranking FIFA (snapshot)
  points: number;
  climate?: number | null; // temperatura típica de adaptación (°C)
}

// Pesos del pronosticador, por factor. Escala relativa (se normaliza al usarlos).
export type FactorKey = 'ranking' | 'form' | 'goals' | 'climate' | 'h2h';
export type ModelWeights = Record<FactorKey, number>;
export const DEFAULT_WEIGHTS: ModelWeights = {
  ranking: 0.4,
  form: 0.25,
  goals: 0.2,
  climate: 0.1,
  h2h: 0.05,
};
export interface ModelTuning {
  weights: ModelWeights;
  accuracy: Partial<Record<FactorKey, number | null>>;
  sample: number;
  note: string;
}
// Datos ricos de ESPN para un partido (stats, alineaciones, eventos, árbitro, asistencia, previa).
export interface MatchExtra {
  available: boolean;
  stats?: MatchStat[];
  lineups?: { team1: MatchLineup | null; team2: MatchLineup | null };
  events?: MatchKeyEvent[];
  referee?: string | null;
  attendance?: number | null;
  form?: { team1: FormGame[]; team2: FormGame[] };
  h2h?: H2H | null;
  ranks?: { team1: TeamRank | null; team2: TeamRank | null };
}

export interface Announcement {
  id: string;
  type: 'modal' | 'banner';
  title: string;
  body: string;
  image: string | null; // URL de imagen opcional (publicidad)
  link: { url: string; label: string } | null; // botón de acción opcional
  frequency: 'once' | 'always' | 'everyNDays'; // cada cuánto se le muestra al mismo device
  everyDays: number; // usado si frequency = everyNDays
  closeDelay: number; // segundos de cuenta regresiva antes de habilitar el cierre
}

// Aviso tal cual se guarda/edita en el panel (incluye active/start/end, que el público no expone).
export interface AdminAnnouncement {
  id: string;
  active: boolean;
  type: 'modal' | 'banner';
  title: string;
  body: string;
  image: string | null;
  link: { url: string; label: string } | null;
  start: string | null;
  end: string | null;
  frequency: 'once' | 'always' | 'everyNDays';
  everyDays: number;
  closeDelay: number;
}

export interface StandingRow {
  position: number;
  team: string;
  pj: number;
  g: number;
  e: number;
  p: number;
  gf: number;
  gc: number;
  dg: number;
  pts: number;
}

export interface GroupData {
  group: string;
  table: StandingRow[];
  matches: Match[];
}

export interface BracketStage {
  key: string;
  label: string;
  order: number;
  matches: Match[];
}

export interface Tournament {
  name: string;
  host: string;
  total: number;
  played: number;
  remaining: number;
  phase: 'group' | 'knockout';
  nextMatch: Match | null;
}

export interface Scorer {
  position: number;
  name: string;
  team: string;
  goals: number;
  penalties: number;
}

export interface MatchSummary {
  id: number;
  team1: string;
  team2: string;
  ft: [number, number];
  diff: number;
  total: number;
  round: string | null;
}

export interface HatTrick {
  player: string;
  team: string;
  goals: number;
  matchId: number;
}

export interface TournamentStats {
  played: number;
  goals: number;
  avg: number;
  penalties: number;
  ownGoals: number;
  scorelessDraws: number;
  draws: number;
  biggestWin: MatchSummary | null;
  mostGoals: MatchSummary | null;
  hatTricks: HatTrick[];
  goalsByMinute: Record<string, number>;
}

export interface ThirdRow extends StandingRow {
  group: string;
  rank: number;
  qualified: boolean;
}

export interface TeamListItem {
  name: string;
  group: string;
}

export interface TeamDetail {
  team: string;
  group: string | null;
  standing: StandingRow | null;
  matches: Match[];
}

export interface HistBigWin {
  team1: string;
  team2: string;
  ft: [number, number];
  diff: number;
}

export interface EditionSummary {
  year: number;
  host: string;
  champion: string | null;
  teams: number;
  matches: number;
  goals: number;
  avg: number;
  penalties: number;
  ownGoals: number;
  scorelessDraws: number;
  yellowCards: number;
  redCards: number;
  shootouts: number;
  hatTricks: number;
  attendanceTotal: number | null;
  attendanceAvg: number | null;
  goldenBoot: { name: string; team: string; goals: number | null } | null;
  biggestWin: HistBigWin | null;
}

export interface UsageSummary {
  usuariosUnicos: number;
  ingresos: number;
  paginasVistas: number;
  minutosTotales: number;
  usuarios: { nick: string; accesos: number; minutos: number; paginas: number }[];
  paginasTop: Record<string, number>;
  porPais: Record<string, number>;
  porDia: Record<string, { sessions: number; minutes: number }>;
}

// --- Timba (prode de la fase de llave) ---

export interface TimbaPrediction {
  num: number;
  g1: number;
  g2: number;
  pen: number | null; // 1|2 = a quién se le dio la tanda (solo si se predijo empate)
  points?: number; // total ya con booster + bonus (presente si el partido ya terminó)
  base?: number; // puntos del marcador sin multiplicar
  booster?: number; // x1/x2/x3/x4 confirmado
  bonus?: number; // bonus de llave confirmado (+2 si clavó los dos equipos)
}

// Árbol de la llave: por cada nº de partido, los dos cruces que lo alimentan ('W74'|'L101').
export type Feeders = Record<number, [string, string]>;

export interface TimbaStanding {
  nick: string;
  pub: string;
  points: number;
  exacts: number; // marcadores exactos acertados
  played: number; // partidos ya jugados que pronosticó
}

export interface TimbaTournamentDetail {
  id: string;
  name: string;
  owner: string | null;
  members: number;
  standings: TimbaStanding[];
}

export interface TimbaTournamentRef {
  id: string;
  name: string;
  token?: string; // solo en torneos privados (para compartir el link)
  global: boolean;
}

// Vista admin (/uso): metadatos de un torneo privado.
export interface TimbaAdminTournament {
  id: string;
  name: string;
  owner: string;
  ownerNick: string;
  created: string;
  members: number;
}

export interface TimbaPredictBody {
  pub: string;
  nick: string;
  num: number;
  g1: number;
  g2: number;
  pen: number | null;
  sig: string;
}

export const api = {
  tournament: () => get<Tournament>('/tournament'),
  today: () =>
    get<{ live: Match[]; upcoming: Match[]; recent: Match[]; liveStatus?: LiveStatus }>('/today'),
  fixtures: () => get<{ matches: Match[]; liveStatus?: LiveStatus }>('/fixtures'),
  match: (id: number | string) => get<{ match: Match; liveStatus?: LiveStatus }>(`/match/${id}`),
  groups: () => get<{ groups: GroupData[] }>('/groups'),
  bracket: () => get<{ stages: BracketStage[]; feeders: Feeders }>('/bracket'),
  teams: () => get<{ teams: TeamListItem[] }>('/teams'),
  team: (name: string) => get<TeamDetail>(`/team/${encodeURIComponent(name)}`),
  scorers: () => get<{ scorers: Scorer[] }>('/scorers'),
  stats: () => get<{ stats: TournamentStats }>('/stats'),
  thirds: () => get<{ projected: boolean; thirds: ThirdRow[] }>('/thirds'),
  history: () => get<{ editions: EditionSummary[] }>('/history'),
  historyYear: (year: number | string) =>
    get<{
      summary: EditionSummary;
      topScorers: { name: string; team: string; goals: number }[];
      biggestWin: HistBigWin | null;
    }>(`/history/${year}`),
  trackSummary: (key: string) =>
    get<UsageSummary>(`/track/summary?key=${encodeURIComponent(key)}`),
  weather: (lat: number, lon: number) => getJson<Weather>(`/weather?lat=${lat}&lon=${lon}`),
  modelTuning: () => getJson<ModelTuning>('/model/tuning'),
  matchDetail: (id: number | string) => getJson<MatchExtra>(`/match/${id}/detail`),
  announcements: () => get<{ announcements: Announcement[] }>('/announcements'),
  announcementsAll: (key: string) =>
    getJson<{ announcements: AdminAnnouncement[] }>(`/announcements/all?key=${encodeURIComponent(key)}`),
  announcementsSave: (key: string, announcements: AdminAnnouncement[]) =>
    post<{ ok: boolean; count: number }>(`/announcements/save?key=${encodeURIComponent(key)}`, { announcements }),
  uploadAnnouncementImage: (key: string, file: File) => {
    const fd = new FormData();
    fd.append('image', file);
    return postForm<{ ok: boolean; url: string }>(`/announcements/upload?key=${encodeURIComponent(key)}`, fd);
  },

  // Timba — datos por usuario, sin cache offline (deben ser frescos).
  // Las escrituras agregan `auth` (credencial de escritura, ver getWriteToken): el backend hace
  // TOFU para que nadie pueda escribir con la identidad de otro. `token` (en join) es el de invitación.
  timbaPredict: async (body: TimbaPredictBody) =>
    post<{ ok: boolean; nick?: string }>('/timba/predict', { ...body, auth: await getWriteToken() }),
  timbaPredictions: (pub: string) =>
    getJson<{ predictions: TimbaPrediction[] }>(`/timba/predictions?pub=${pub}`),
  timbaTournaments: (pub: string) =>
    getJson<{ tournaments: TimbaTournamentRef[] }>(`/timba/tournaments?pub=${pub}`),
  // pub para torneos privados (membresía); el global no lo necesita. key = admin (panel /uso, ve cualquiera).
  timbaTournament: (id: string, pub = '', key = '') => {
    const qs = [pub && `pub=${pub}`, key && `key=${encodeURIComponent(key)}`].filter(Boolean).join('&');
    return getJson<TimbaTournamentDetail>(`/timba/tournament/${id}${qs ? `?${qs}` : ''}`);
  },
  timbaAdminTournaments: (key: string) =>
    getJson<{ tournaments: TimbaAdminTournament[] }>(`/timba/admin/tournaments?key=${encodeURIComponent(key)}`),
  timbaPlayer: (pub: string) => getJson<{ nick: string }>(`/timba/player?pub=${pub}`),
  timbaSetNick: async (pub: string, nick: string) =>
    post<{ nick: string; saved: boolean }>('/timba/nick', { pub, nick, auth: await getWriteToken() }),
  timbaAdminDeletePlayer: (pub: string, key: string) =>
    post<{ ok: boolean }>(`/timba/admin/delete-player?key=${encodeURIComponent(key)}`, { pub }),
  timbaCreateTournament: async (pub: string, nick: string, name: string) =>
    post<{ id: string; token: string; name: string; nick?: string }>('/timba/tournament', { pub, nick, name, auth: await getWriteToken() }),
  timbaJoinTournament: async (pub: string, nick: string, token: string) =>
    post<{ id: string; name: string; nick?: string }>('/timba/tournament/join', { pub, nick, token, auth: await getWriteToken() }),
};
